// src/services/pathLabTestReportService.js
import apiClient from "./apiClient";

const API_URL = `/pathlabreports`; // Use relative path since apiClient has baseURL 


export const createReport = async (pathlabreport) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.post(API_URL, pathlabreport);
    invalidateOrgPathLabReportsLiteCache();
    return response.data;
  } catch (error) {
    console.error("Error creating path lab report:", error);
    throw error; 
  }
};

export const getPathPabReportByPatient = async (patientId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_URL}/patient/${patientId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching path lab report:", error);
    throw error;
  }
};

export const getAllReports = async () => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(API_URL);
    return response.data;
  } catch (error) {
    console.error("Error fetching all reports:", error);
    throw error;
  }
};

const stripSampleTransactionMeta = (reportData = {}) => {
  const sampleMeta = reportData._sampleTransactionMeta || reportData.sampleTransactionMeta || null;
  const apiPayload = { ...reportData };
  delete apiPayload._sampleTransactionMeta;
  delete apiPayload.sampleTransactionMeta;
  return { apiPayload, sampleMeta };
};

const syncResultSampleTransaction = async (reportId, reportData, responseData, sampleMeta) => {
  try {
    const { syncSampleTransactionAfterResult } = await import("./sampleTransactionService");
    await syncSampleTransactionAfterResult(reportId, reportData, responseData, sampleMeta);
  } catch (error) {
    console.warn(
      "[pathLabTestReportService] Sample transaction result sync failed:",
      error?.message || error
    );
  }
};

export const updateReport = async (id, patient) => {
  const { apiPayload, sampleMeta } = stripSampleTransactionMeta(patient);
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.put(`${API_URL}/${id}`, apiPayload);
    invalidateOrgPathLabReportsLiteCache();
    await syncResultSampleTransaction(id, apiPayload, response.data, sampleMeta);
    return response.data;
  } catch (error) {
    console.error("Error updating report:", error);
    throw error;
  }
};

export const updateReportStatus = async (reportId, status, result) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.put(
      `${API_URL}/${reportId}/status`, 
      { resultStatus: status,
        result // Pass result (can be null)
       } // Ensure this is a simple object with just resultStatus
    );
    invalidateOrgPathLabReportsLiteCache();
    return response.data;
  } catch (error) {
    console.error("Error updating report status:", error);
    throw error;
  }
};

export const updateReportResult = async (reportId, reportData, retryCount = 0) => {
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000; // 1 second base delay
  const { apiPayload, sampleMeta } = stripSampleTransactionMeta(reportData);
  
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.put(`${API_URL}/${reportId}/result`, apiPayload);
    invalidateOrgPathLabReportsLiteCache();
    await syncResultSampleTransaction(reportId, apiPayload, response.data, sampleMeta);
    return response.data;
  } catch (error) {
    // Check if it's a concurrency/transaction error that can be retried
    const errorMessage = error?.response?.data?.message || error?.message || '';
    const isRetryableError = errorMessage.includes('Row was updated or deleted') || 
                            errorMessage.includes('another transaction') ||
                            errorMessage.includes('unsaved-value mapping');
    
    if (isRetryableError && retryCount < MAX_RETRIES) {
      const delay = RETRY_DELAY * Math.pow(2, retryCount); // Exponential backoff
      console.warn(`[pathLabTestReportService] Retryable error detected. Retrying in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
      return updateReportResult(reportId, reportData, retryCount + 1);
    }
    
    const backendMessage = error?.response?.data?.message || error?.message || "";
    if (
      String(backendMessage).includes("rollback-only") ||
      String(backendMessage).includes("Transaction silently rolled back")
    ) {
      console.error(
        "[pathLabTestReportService] Result save rolled back on backend (sample-transaction sync). " +
          "Restart backend with the direct-UPDATE result sync fix, then save again.",
        error
      );
    } else {
      console.error("Error updating report:", error);
    }
    throw error;
  }
};

export const updateReportStatusReject = async (reportId) => {
    try {
        // Get organizationId to ensure it's available
        const storedUser = localStorage.getItem("user");
        let organizationId = null;
        if (storedUser) {
            try {
                const user = JSON.parse(storedUser);
                organizationId = user?.organizationId || localStorage.getItem("organizationId");
                if (organizationId && typeof organizationId === 'string') {
                    organizationId = parseInt(organizationId, 10);
                }
            } catch (e) {
                // Ignore parse errors
            }
        }
        
        // apiClient will automatically add organizationId via interceptor
        // But for this endpoint, we also explicitly add it as query param to ensure backend filters correctly
        const config = organizationId && organizationId > 0 
            ? { params: { organizationId } }
            : {};
        
        const response = await apiClient.put(`${API_URL}/${reportId}/reject`, {}, config);
        invalidateOrgPathLabReportsLiteCache();
        return response.data;
    } catch (error) {
        console.error("Error rejecting report:", error);
        throw error;
    }
};

export const getBilledTests = async () => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_URL}/billed-tests`); // Adjust the endpoint as necessary
    return response.data;
  } catch (error) {
    console.error("Error fetching billed tests:", error);
    throw error;
  }
};

export const getCompletedTests = async () => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_URL}/completed-tests`); // Adjust the endpoint as necessary
    return response.data;
  } catch (error) {
    console.error("Error fetching completed tests:", error);
    throw error;
  }
};

/** Avoid full GET /pathlabreports. Cache completed+billed; merge without dropping filled fields. */
let orgReportsLiteCache = null;
let orgReportsLiteCacheTime = 0;
let orgReportsFullCache = null;
let orgReportsFullCacheTime = 0;
let completedDoctorNameCache = null;
let completedDoctorNameCacheTime = 0;
const ORG_REPORTS_LITE_TTL_MS = 90 * 1000;

const hasFilledText = (value) =>
  value != null && String(value).trim() !== "" && String(value).trim().toLowerCase() !== "null";

const RESULT_STATUS_RANK = {
  REJECT: 0,
  REJECTED: 0,
  PENDING: 1,
  BILLED: 2,
  COLLECTED: 3,
  COMPLETE: 4,
  COMPLETED: 4,
};

const getResultStatusRank = (status) => {
  if (status == null || status === "") return -1;
  return RESULT_STATUS_RANK[String(status).trim().toUpperCase()] ?? -1;
};

/** Prefer non-null fields so patient API nulls don't wipe doctorName / result from completed-tests. */
export const mergePathLabReportPreferFilled = (base, overlay) => {
  if (!base) return overlay;
  if (!overlay) return base;

  const pick = (primary, fallback) =>
    hasFilledText(primary) ? primary : hasFilledText(fallback) ? fallback : primary ?? fallback;

  const baseStatus = base.resultStatus ?? base.result_status;
  const overlayStatus = overlay.resultStatus ?? overlay.result_status;
  const baseRank = getResultStatusRank(baseStatus);
  const overlayRank = getResultStatusRank(overlayStatus);

  // Never let an older workflow status overwrite a more advanced one (e.g. BILLED over COMPLETE).
  let mergedStatus = baseStatus ?? overlayStatus;
  if (overlayRank > baseRank) {
    mergedStatus = overlayStatus;
  } else if (baseRank > overlayRank) {
    mergedStatus = baseStatus;
  } else {
    mergedStatus = pick(overlayStatus, baseStatus);
  }

  let mergedResult = pick(overlay.result, base.result);
  // Later source (overlay) wins when it has a filled result at the same or higher status.
  // Fixes OPD table showing stale org/prescription results while patient API (edit modal) is fresh.
  if (hasFilledText(overlay.result) && overlayRank >= baseRank) {
    mergedResult = overlay.result;
  } else if (hasFilledText(base.result) && baseRank > overlayRank) {
    mergedResult = base.result;
  } else if (hasFilledText(overlay.result)) {
    mergedResult = overlay.result;
  } else if (hasFilledText(base.result)) {
    mergedResult = base.result;
  }

  const baseName = String(base.testName ?? base.test_name ?? "")
    .trim()
    .toLowerCase();
  const overlayName = String(overlay.testName ?? overlay.test_name ?? "")
    .trim()
    .toLowerCase();
  const namesConflict =
    Boolean(baseName) && Boolean(overlayName) && baseName !== overlayName;

  // Same reportId with conflicting test names: keep identity from the side whose
  // result we are keeping so CBC rows cannot inherit Thyroid catalog ids/names.
  const resultFromOverlay =
    hasFilledText(mergedResult) &&
    hasFilledText(overlay.result) &&
    String(mergedResult) === String(overlay.result);
  const identitySource = namesConflict
    ? resultFromOverlay
      ? overlay
      : base
    : overlay;

  return {
    ...base,
    ...overlay,
    reportId: overlay.reportId ?? overlay.id ?? base.reportId ?? base.id,
    id: overlay.reportId ?? overlay.id ?? base.reportId ?? base.id,
    testId:
      identitySource.testId ??
      identitySource.labTestId ??
      overlay.testId ??
      overlay.labTestId ??
      base.testId ??
      base.labTestId,
    labTestId:
      identitySource.labTestId ??
      identitySource.testId ??
      overlay.labTestId ??
      overlay.testId ??
      base.labTestId ??
      base.testId,
    testName: pick(
      identitySource.testName ?? identitySource.test_name,
      pick(overlay.testName ?? overlay.test_name, base.testName ?? base.test_name)
    ),
    result: mergedResult,
    resultStatus: mergedStatus,
    doctorId: overlay.doctorId ?? base.doctorId ?? null,
    doctorName: pick(overlay.doctorName, base.doctorName),
    assignedByName: pick(overlay.assignedByName, base.assignedByName),
    patientId: overlay.patientId ?? overlay.patient_id ?? base.patientId ?? base.patient_id,
    patientName: pick(overlay.patientName, base.patientName),
    reportDate: pick(overlay.reportDate ?? overlay.report_date, base.reportDate ?? base.report_date),
    normalRange: pick(overlay.normalRange, base.normalRange),
    prescriptionId:
      overlay.prescriptionId ??
      overlay.prescription_id ??
      base.prescriptionId ??
      base.prescription_id ??
      null,
  };
};

const upsertReportById = (byId, report) => {
  const id = report?.reportId ?? report?.id;
  if (id == null) return;
  const existing = byId.get(id);
  byId.set(id, existing ? mergePathLabReportPreferFilled(existing, report) : report);
};

/**
 * Org report hydrate for OPD / patient views.
 * Uses GET /pathlabreports + billed-tests (cached). Optionally merges patient-scoped rows.
 * Does NOT block callers — callers should invoke in background after first paint.
 */
export const getOrgPathLabReportsLite = async ({ patientIds = [], includeCompleted = true } = {}) => {
  const now = Date.now();
  const uniquePatientIds = [
    ...new Set(
      (Array.isArray(patientIds) ? patientIds : [])
        .map((id) => Number(id))
        .filter((id) => !Number.isNaN(id) && id > 0)
    ),
  ];

  let orgReports = [];
  if (orgReportsLiteCache && now - orgReportsLiteCacheTime < ORG_REPORTS_LITE_TTL_MS) {
    orgReports = orgReportsLiteCache;
  } else if (includeCompleted) {
    // GET /pathlabreports already includes completed rows; completed-tests is redundant and much slower.
    orgReports = await getOrgPathLabReportsFull();
  } else {
    const billed = await getBilledTests().catch((error) => {
      console.warn("[pathLabTestReportService] getBilledTests failed:", error);
      return [];
    });
    orgReports = Array.isArray(billed) ? billed : [];
  }

  const byId = new Map();
  (Array.isArray(orgReports) ? orgReports : []).forEach((report) => upsertReportById(byId, report));

  if (uniquePatientIds.length > 0) {
    const CONCURRENCY = 4;
    for (let i = 0; i < uniquePatientIds.length; i += CONCURRENCY) {
      const chunk = uniquePatientIds.slice(i, i + CONCURRENCY);
      const chunkReports = await Promise.all(
        chunk.map((patientId) =>
          getPathPabReportByPatient(patientId).catch((error) => {
            console.warn(
              `[pathLabTestReportService] getPathPabReportByPatient(${patientId}) failed:`,
              error
            );
            return [];
          })
        )
      );
      chunkReports.flat().forEach((report) => upsertReportById(byId, report));
    }
  }

  return Array.from(byId.values());
};

/**
 * Full org report list for queue screens (OpdIpdPatients, AllPatientTest).
 * Uses GET /pathlabreports + billed-tests only — avoids the slow completed-tests endpoint.
 */
export const getOrgPathLabReportsFull = async () => {
  const now = Date.now();
  if (orgReportsFullCache && now - orgReportsFullCacheTime < ORG_REPORTS_LITE_TTL_MS) {
    return orgReportsFullCache;
  }

  const [allReports, billed] = await Promise.all([
    getAllReports().catch((error) => {
      console.warn("[pathLabTestReportService] getAllReports failed:", error);
      return [];
    }),
    getBilledTests().catch((error) => {
      console.warn("[pathLabTestReportService] getBilledTests failed:", error);
      return [];
    }),
  ]);

  const byId = new Map();
  [...(Array.isArray(billed) ? billed : []), ...(Array.isArray(allReports) ? allReports : [])].forEach(
    (report) => upsertReportById(byId, report)
  );

  const merged = Array.from(byId.values());
  orgReportsFullCache = merged;
  orgReportsFullCacheTime = Date.now();
  orgReportsLiteCache = merged;
  orgReportsLiteCacheTime = Date.now();
  return orgReportsFullCache;
};

/** Reports for one patient: patient API + billed + cached org list (never loads org-wide completed-tests). */
export const getMergedPathLabReportsForPatient = async (patientId) => {
  if (patientId == null || patientId === "") return [];

  const pid = Number(patientId);
  const now = Date.now();
  const cachedOrg =
    orgReportsFullCache && now - orgReportsFullCacheTime < ORG_REPORTS_LITE_TTL_MS
      ? orgReportsFullCache
      : null;

  const [direct, billed] = await Promise.all([
    getPathPabReportByPatient(patientId).catch((error) => {
      console.warn("[pathLabTestReportService] getPathPabReportByPatient failed:", error);
      return [];
    }),
    getBilledTests().catch((error) => {
      console.warn("[pathLabTestReportService] getBilledTests failed:", error);
      return [];
    }),
  ]);

  const byId = new Map();
  // Apply cached org first as a baseline, then billed, then patient-scoped API last
  // so freshly saved results/status are not overwritten by a stale org cache.
  if (cachedOrg) {
    cachedOrg
      .filter((r) => Number(r?.patientId ?? r?.patient_id) === pid)
      .forEach((report) => upsertReportById(byId, report));
  }
  (Array.isArray(billed) ? billed : [])
    .filter((r) => Number(r?.patientId ?? r?.patient_id) === pid)
    .forEach((report) => upsertReportById(byId, report));
  (Array.isArray(direct) ? direct : []).forEach((report) => upsertReportById(byId, report));

  return Array.from(byId.values()).sort(
    (a, b) => Number(b.reportId ?? b.id ?? 0) - Number(a.reportId ?? a.id ?? 0)
  );
};

export const invalidateOrgPathLabReportsLiteCache = () => {
  orgReportsLiteCache = null;
  orgReportsLiteCacheTime = 0;
  orgReportsFullCache = null;
  orgReportsFullCacheTime = 0;
  completedDoctorNameCache = null;
  completedDoctorNameCacheTime = 0;
};

/**
 * Report-id → doctorName from completed-tests (cached).
 * Used only to fill doctor names missing on GET /pathlabreports rows — does not replace list data.
 */
export const getCompletedTestDoctorNameMap = async () => {
  const now = Date.now();
  if (completedDoctorNameCache && now - completedDoctorNameCacheTime < ORG_REPORTS_LITE_TTL_MS) {
    return completedDoctorNameCache;
  }

  const completed = await getCompletedTests().catch((error) => {
    console.warn("[pathLabTestReportService] getCompletedTests (doctor names) failed:", error);
    return [];
  });

  const map = new Map();
  (Array.isArray(completed) ? completed : []).forEach((report) => {
    const id = report?.reportId ?? report?.id;
    const name = report?.doctorName ?? report?.assignedByName;
    if (id != null && hasFilledText(name)) {
      map.set(Number(id), String(name).trim());
    }
  });

  completedDoctorNameCache = map;
  completedDoctorNameCacheTime = Date.now();
  return map;
};

export const updateResultStatusBilled = async (reportId) => {
  try {
    // Get organizationId to ensure it's available
    const storedUser = localStorage.getItem("user");
    let organizationId = null;
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        organizationId = user?.organizationId || localStorage.getItem("organizationId");
        if (organizationId && typeof organizationId === "string") {
          organizationId = parseInt(organizationId, 10);
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    // apiClient will automatically add organizationId via interceptor
    // But we also explicitly add it as query param to ensure backend filters correctly
    const config = organizationId && organizationId > 0 ? { params: { organizationId } } : {};

    const response = await apiClient.put(
      `${API_URL}/${reportId}/status`,
      {
        resultStatus: "BILLED",
        result: null, // Set result to null
      },
      config
    );
    invalidateOrgPathLabReportsLiteCache();
    return response.data;
  } catch (error) {
    console.error("Error updating report status to BILLED:", error);
    throw error;
  }
};

// Set status to COLLECTED for a given report
export const updateReportStatusCollect = async (reportId) => {
  try {
    // Get organizationId to ensure it's available
    const storedUser = localStorage.getItem("user");
    let organizationId = null;
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        organizationId = user?.organizationId || localStorage.getItem("organizationId");
        if (organizationId && typeof organizationId === "string") {
          organizationId = parseInt(organizationId, 10);
        }
      } catch (e) {
        // Ignore parse errors
      }
    }

    // apiClient will automatically add organizationId via interceptor
    // But we also explicitly add it as query param to ensure backend filters correctly
    const config = organizationId && organizationId > 0 ? { params: { organizationId } } : {};

    const response = await apiClient.put(
      `${API_URL}/${reportId}/collected`,
      {
        resultStatus: "COLLECTED",
        result: null,
      },
      config
    );
    invalidateOrgPathLabReportsLiteCache();
    return response.data;
  } catch (error) {
    console.error("Error updating report status to COLLECTED:", error);
    throw error;
  }
};
